/**
 * Tracking Service - Usage Stats & Cost Estimation
 *
 * Provides usage statistics via the backend API and local cost estimation utilities.
 * Generation logging is handled server-side by the Gemini route handlers.
 *
 * All API operations go through the backend (no direct Supabase calls).
 */

import { apiGet } from '@/services/apiClient';

// ============================================================================
// TYPES
// ============================================================================

export type OperationType =
  | 'text_generation'
  | 'image_generation'
  | 'image_stylize'
  | 'image_edit'
  | 'content_refine'
  | 'document_process';

export interface UsageStats {
  totalOperations: number;
  textGenerations: number;
  imageGenerations: number;
  imageEdits: number;
  contentRefines: number;
  totalCostUsd: number;
  totalDurationMs: number;
  projectsCreated: number;
}

// ============================================================================
// COST ESTIMATION
// Prices based on public Google Gemini API pricing (approximate)
// Updated: 2025 pricing
// ============================================================================

const COST_PER_1K_INPUT: Record<string, number> = {
  // Gemini models
  'gemini-2.5-pro': 0.00125,
  'gemini-3-pro-image-preview': 0.00125,
  'gemini-2.5-flash': 0.000075,
  'gemini-2.5-flash-image': 0.000075,
  // Claude models
  'claude-sonnet-4-6': 0.003,
  'claude-haiku-4-5': 0.0008,
};

const COST_PER_1K_OUTPUT: Record<string, number> = {
  // Gemini models
  'gemini-2.5-pro': 0.01,
  'gemini-3-pro-image-preview': 0.01,
  'gemini-2.5-flash': 0.0003,
  'gemini-2.5-flash-image': 0.0003,
  // Claude models
  'claude-sonnet-4-6': 0.015,
  'claude-haiku-4-5': 0.004,
};

// Fixed cost per image generation (approximate)
const COST_PER_IMAGE: Record<string, number> = {
  'gemini-3-pro-image-preview': 0.04,
  'gemini-2.5-flash-image': 0.02,
};

/**
 * Estimates the cost of an AI operation based on model and token counts.
 */
export const estimateCost = (
  model: string,
  operationType: OperationType,
  inputTokens: number = 0,
  outputTokens: number = 0
): number => {
  // Image generation has a fixed cost
  if (operationType === 'image_generation' || operationType === 'image_stylize' || operationType === 'image_edit') {
    return COST_PER_IMAGE[model] || 0.03;
  }

  // Text operations: cost based on tokens
  const inputCostPer1k = COST_PER_1K_INPUT[model] || 0.001;
  const outputCostPer1k = COST_PER_1K_OUTPUT[model] || 0.005;

  return (inputTokens / 1000) * inputCostPer1k + (outputTokens / 1000) * outputCostPer1k;
};

/**
 * Estimates input token count from a text prompt.
 * Rough approximation: ~4 characters per token for English text.
 */
export const estimateTokens = (text: string): number => {
  return Math.ceil(text.length / 4);
};

// ============================================================================
// USAGE STATS (via backend API)
// ============================================================================

const EMPTY_STATS: UsageStats = {
  totalOperations: 0,
  textGenerations: 0,
  imageGenerations: 0,
  imageEdits: 0,
  contentRefines: 0,
  totalCostUsd: 0,
  totalDurationMs: 0,
  projectsCreated: 0,
};

/**
 * Gets usage statistics for the current user via the backend API.
 * Can be filtered by date range.
 */
export const getUsageStats = async (
  startDate?: string,
  endDate?: string
): Promise<UsageStats> => {
  try {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    return await apiGet<UsageStats>('/api/usage/stats', params);
  } catch (error: any) {
    return { ...EMPTY_STATS };
  }
};
